import dayjs from 'dayjs';
import { INTERVAL_TEN_MINUTES, INTERVAL_ONE_DAY, INTERVAL_ONE_WEEK } from '../../Constants';
import { ChartPlotLine } from '../../../types/ChartPlotLine';
import { resolveUserTimezone } from '../../../helpers/timeRangeUtils';
import { getMinMAx } from '../../../helpers/utils';
import Helper from '../../../helpers/Helper';
import { EChartsCoreOption } from './echartsCore';
import { fillGaps } from './gapFill';
import { formatTimeSeriesTooltip, positionTooltip, TOOLTIP_BASE, TooltipParamLike } from './tooltipAdapter';
import { EChartBandPoint, EChartPoint, EChartThreshold, EChartTimeSeries, EChartYAxis, TimeSeriesOptionArgs } from './types';
import { VERTICAL_BAR_BOTTOM_RADIUS, VERTICAL_BAR_TOP_RADIUS } from './barStyle';

export const BAND_BASE_PREFIX = '__band_base__';
const LEGEND_MAX_HEIGHT = 34;
const LEGEND_GRID_RESERVE = 38;
const AXIS_NAME_GAP = 52;
const LARGE_BAR_THRESHOLD = 5000;
const BAR_CATEGORY_GAP = '43%';
const BAR_MIN_WIDTH = 2;
const AXIS_NAME_RESERVE = 22;

const DASH_STYLES: { [key: string]: string | number[] } = {
    dot: [1, 3],
    shortdot: 'dotted',
    dash: 'dashed',
    shortdash: 'dashed',
    longdash: 'dashed',
    solid: 'solid',
};

export const DAY_LABEL_FORMAT = 'D MMM';
const HOURLY_LABEL_LIMIT = INTERVAL_ONE_WEEK + INTERVAL_ONE_DAY;
export const AXIS_SPLIT_NUMBER = 11;
const TARGET_TICKS = 16;

type TickUnit = 'second' | 'minute' | 'hour' | 'day' | 'month';

const TICK_STEPS: { ms: number; amount: number; unit: TickUnit }[] = [
    { ms: 1000, amount: 1, unit: 'second' },
    { ms: 2000, amount: 2, unit: 'second' },
    { ms: 5000, amount: 5, unit: 'second' },
    { ms: 10000, amount: 10, unit: 'second' },
    { ms: 15000, amount: 15, unit: 'second' },
    { ms: 30000, amount: 30, unit: 'second' },
    { ms: 60000, amount: 1, unit: 'minute' },
    { ms: 120000, amount: 2, unit: 'minute' },
    { ms: 300000, amount: 5, unit: 'minute' },
    { ms: 600000, amount: 10, unit: 'minute' },
    { ms: 900000, amount: 15, unit: 'minute' },
    { ms: 1800000, amount: 30, unit: 'minute' },
    { ms: 3600000, amount: 1, unit: 'hour' },
    { ms: 7200000, amount: 2, unit: 'hour' },
    { ms: 10800000, amount: 3, unit: 'hour' },
    { ms: 21600000, amount: 6, unit: 'hour' },
    { ms: 43200000, amount: 12, unit: 'hour' },
    { ms: 86400000, amount: 1, unit: 'day' },
    { ms: 172800000, amount: 2, unit: 'day' },
    { ms: 604800000, amount: 7, unit: 'day' },
    { ms: 1209600000, amount: 14, unit: 'day' },
    { ms: 2592000000, amount: 1, unit: 'month' },
];

export const buildAxisTicks = (
    min: number | undefined, max: number | undefined, stepMs: number, timezone: string,
): number[] => {
    if (min === undefined || max === undefined || !(max > min)) {
        return [];
    }
    const target = (max - min) / TARGET_TICKS;
    const step = TICK_STEPS.find(candidate => candidate.ms >= target && candidate.ms >= stepMs)
        ?? TICK_STEPS[TICK_STEPS.length - 1];
    const at = (value: number) => (timezone === 'UTC' ? dayjs.utc(value) : dayjs(value).tz(timezone));

    let cursor = at(min).startOf(step.unit === 'month' ? 'month' : 'day');
    if (step.unit === 'second' || step.unit === 'minute' || step.unit === 'hour') {
        const skip = Math.floor((min - cursor.valueOf()) / step.ms);
        cursor = cursor.add(skip * step.amount, step.unit);
    }

    const ticks: number[] = [];
    while (cursor.valueOf() <= max && ticks.length <= TARGET_TICKS * 2) {
        if (cursor.valueOf() >= min) {
            ticks.push(cursor.valueOf());
        }
        cursor = cursor.add(step.amount, step.unit);
    }
    return ticks;
};

export const axisLabelFormat = (spanMs: number): string => {
    if (spanMs <= INTERVAL_TEN_MINUTES) return 'HH:mm:ss';
    if (spanMs <= HOURLY_LABEL_LIMIT) return 'HH:mm';
    return DAY_LABEL_FORMAT;
};

export const formatAxisTime = (value: number, format: string, timezone: string): string => {
    return timezone === 'UTC' ? dayjs.utc(value).format(format) : dayjs(value).tz(timezone).format(format);
};

export const formatAxisLabel = (value: number, format: string, timezone: string): string => {
    if (format === DAY_LABEL_FORMAT) {
        return formatAxisTime(value, format, timezone);
    }
    const moment = timezone === 'UTC' ? dayjs.utc(value) : dayjs(value).tz(timezone);
    return moment.valueOf() === moment.startOf('day').valueOf()
        ? moment.format(DAY_LABEL_FORMAT)
        : moment.format(format);
};

export const getAxisExtent = (timeRangeContext: TimeSeriesOptionArgs['timeRangeContext']) => {
    const { minX, maxX } = getMinMAx(timeRangeContext);
    const timeRange = timeRangeContext.timeRange;
    const stepMs = Helper.getInterval(timeRange?.from, timeRange?.to, timeRange?.interval);

    if (!minX || !maxX) {
        return { min: minX, max: maxX, stepMs };
    }

    return {
        min: Math.floor(minX / stepMs) * stepMs,
        max: Math.ceil(maxX / stepMs) * stepMs,
        stepMs,
    };
};

const STACK_ID = 'total';
const HOVER_SYMBOL_SIZE = 8;

const buildBand = (set: EChartTimeSeries): any[] => {
    const points = set.data as EChartBandPoint[];
    const stack = `band-${set.name}`;
    const shared = {
        type: 'line',
        stack,
        symbol: 'none',
        silent: true,
        lineStyle: { opacity: 0 },
        connectNulls: false,
    };
    return [
        {
            ...shared,
            name: `${BAND_BASE_PREFIX}${set.name}`,
            data: points.map(point => [point[0], point[1]]),
            areaStyle: { opacity: 0 },
        },
        {
            ...shared,
            name: set.name,
            data: points.map(point => ({
                value: [point[0], point[2] - point[1]],
                low: point[1],
                high: point[2],
            })),
            areaStyle: { color: set.color, opacity: set.fillOpacity ?? 0.4 },
        },
    ];
};

const isStacked = (set: EChartTimeSeries) => set.chartType === 'bar' || set.chartType === 'area';

const alignToGrid = (data: EChartPoint[], grid: number[], missing: number | null): EChartPoint[] => {
    const byTime = new Map<number, EChartPoint>();
    data.forEach(point => byTime.set(point[0], point));
    return grid.map(time => byTime.get(time) ?? [time, missing]);
};

const buildStackGrid = (prepared: (EChartPoint[] | null)[], series: EChartTimeSeries[]): number[] | null => {
    const stacked = prepared.filter((data, index) => data && isStacked(series[index])) as EChartPoint[][];
    if (stacked.length < 2) {
        return null;
    }
    const times = new Set<number>();
    stacked.forEach(data => data.forEach(point => times.add(point[0])));
    return Array.from(times).sort((first, second) => first - second);
};

const buildSeries = (series: EChartTimeSeries[], stepMs: number): any[] => {
    const prepared = series.map(set => (set.chartType === 'band' ? null : fillGaps(set.data as EChartPoint[], stepMs)));
    const grid = buildStackGrid(prepared, series);

    return series.flatMap((set, index): any[] => {
        if (set.chartType === 'band') {
            return buildBand(set);
        }
        const filled = prepared[index] as EChartPoint[];
        const data = grid && isStacked(set)
            ? alignToGrid(filled, grid, set.chartType === 'area' ? 0 : null)
            : filled;
        const shared = {
            name: set.name,
            data,
            yAxisIndex: set.yAxisIndex ?? 0,
            color: set.color,
        };

        if (set.chartType === 'bar') {
            return [{ ...shared, type: 'bar', stack: set.stack ?? STACK_ID, barCategoryGap: BAR_CATEGORY_GAP,
                barMinWidth: BAR_MIN_WIDTH, large: true, largeThreshold: LARGE_BAR_THRESHOLD }];
        }

        const hoverMarker = {
            symbol: 'circle',
            symbolSize: HOVER_SYMBOL_SIZE,
            emphasis: {
                scale: 1.5,
                itemStyle: { color: set.color, borderColor: '#ffffff', borderWidth: 1 },
            },
        };

        if (set.chartType === 'area') {
            return [{
                ...shared,
                ...hoverMarker,
                type: 'line',
                stack: set.stack ?? STACK_ID,
                smooth: set.smooth ?? false,
                showSymbol: false,
                connectNulls: false,
                lineStyle: { width: 1 },
                areaStyle: { opacity: set.fillOpacity ?? 0.7 },
            }];
        }

        return [{
            ...shared,
            ...hoverMarker,
            type: 'line',
            smooth: set.smooth ?? true,
            showSymbol: false,
            connectNulls: false,
            lineStyle: { width: 2 },
        }];
    });
};

const reverseStacks = (series: any[]): any[] => {
    const stackSlots: number[] = [];
    series.forEach((set, index) => {
        if (set.stack === STACK_ID) {
            stackSlots.push(index);
        }
    });
    if (stackSlots.length < 2) {
        return series;
    }
    const result = series.slice();
    stackSlots.forEach((slot, position) => {
        result[slot] = series[stackSlots[stackSlots.length - 1 - position]];
    });
    return result;
};

const roundExposedBarSegments = (series: any[]): any[] => {
    const claimedEnds = new Set<string>();
    const radiusByPoint = new Map<string, readonly number[]>();

    for (let seriesIndex = series.length - 1; seriesIndex >= 0; seriesIndex--) {
        const set = series[seriesIndex];
        if (set.type !== 'bar') continue;

        const stack = set.stack ?? `series-${seriesIndex}`;
        const axis = set.yAxisIndex ?? 0;
        set.data.forEach((point: EChartPoint, pointIndex: number) => {
            const [time, value] = point;
            if (typeof value !== 'number' || value === 0) return;

            // Positive and negative values stack away from zero in opposite directions.
            const direction = value > 0 ? 'positive' : 'negative';
            const endKey = `${axis}:${stack}:${time}:${direction}`;
            if (claimedEnds.has(endKey)) return;

            claimedEnds.add(endKey);
            radiusByPoint.set(
                `${seriesIndex}:${pointIndex}`,
                value > 0 ? VERTICAL_BAR_TOP_RADIUS : VERTICAL_BAR_BOTTOM_RADIUS,
            );
        });
    }

    return series.map((set, seriesIndex) => set.type !== 'bar' ? set : ({
        ...set,
        data: set.data.map((point: EChartPoint, pointIndex: number) => {
            const borderRadius = radiusByPoint.get(`${seriesIndex}:${pointIndex}`);
            return borderRadius ? { value: point, itemStyle: { borderRadius } } : point;
        }),
    }));
};

export const buildPlotLineSeries = (plotLines: ChartPlotLine[]) => ({
    type: 'line',
    data: [],
    silent: true,
    animation: false,
    tooltip: { show: false },
    markLine: {
        silent: true,
        symbol: 'none',
        animation: false,
        label: { show: false },
        emphasis: { disabled: true },
        data: plotLines.map(plotLine => ({
            xAxis: plotLine.timeValue ?? plotLine.value,
            lineStyle: {
                color: plotLine.color,
                width: plotLine.width,
                type: DASH_STYLES[(plotLine.dashStyle || 'solid').toLowerCase()] || 'solid',
            },
        })),
    },
});

const NUMERIC_SYMBOLS = ['k', 'M', 'G', 'T', 'P', 'E'];
const NUMERIC_MAGNITUDE = 1000;

export const axisNumberLabel = (value: number): string => {
    for (let index = NUMERIC_SYMBOLS.length - 1; index >= 0; index--) {
        const multiple = Math.pow(NUMERIC_MAGNITUDE, index + 1);
        if (value !== 0 && Math.abs(value) >= multiple && (value * 10) % multiple === 0) {
            return `${Number((value / multiple).toFixed(2))}${NUMERIC_SYMBOLS[index]}`;
        }
    }
    if (value === 0) {
        return '0';
    }
    const rounded = Math.abs(value) >= 1 ? Number(value.toFixed(2)) : Number(value.toPrecision(3));
    return Math.abs(rounded) >= 10000
        ? `${rounded}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
        : `${rounded}`;
};

export const yAxisFormatter = (format: 'time' | 'number') => (value: number): string => {
    return format === 'time' ? Helper.getTimeInEnglish(value) : axisNumberLabel(value);
};

const axisSide = (descriptor: EChartYAxis | undefined, index: number) =>
    descriptor?.position ?? (index === 1 ? 'right' : 'left');

const hasNamedAxis = (descriptors: EChartYAxis[] | undefined, side: 'left' | 'right') =>
    !!descriptors?.some((descriptor, index) => descriptor?.name && axisSide(descriptor, index) === side);

const TIME_AXIS_STEPS = [
    1000, 2000, 5000, 10000, 15000, 30000,
    60000, 120000, 300000, 600000, 900000, 1800000,
    3600000, 7200000, 10800000, 21600000, 43200000, 86400000,
];
const TIME_AXIS_TICKS = 6;

const axisPeak = (series: EChartTimeSeries[], axisIndex: number): number => {
    const onAxis = series.filter(set => (set.yAxisIndex ?? 0) === axisIndex && set.chartType !== 'band');
    const totals = new Map<number, number>();
    let peak = 0;
    onAxis.forEach(set => {
        (set.data as EChartPoint[]).forEach(point => {
            const value = typeof point[1] === 'number' ? point[1] : 0;
            if (isStacked(set)) {
                const total = (totals.get(point[0]) ?? 0) + value;
                totals.set(point[0], total);
                peak = Math.max(peak, total);
            } else {
                peak = Math.max(peak, value);
            }
        });
    });
    return peak;
};

const timeAxisInterval = (peak: number): number | undefined => {
    if (!(peak > 0)) {
        return undefined;
    }
    return TIME_AXIS_STEPS.find(step => peak / step <= TIME_AXIS_TICKS);
};

const buildYAxis = (descriptors?: EChartYAxis[], series: EChartTimeSeries[] = []) => {
    return [0, 1].map(index => {
        const descriptor = descriptors?.[index];
        const showLabels = !!descriptor?.labels;
        const peak = descriptor?.format === 'time' && descriptor?.max === undefined
            ? axisPeak(series, index)
            : 0;
        const interval = timeAxisInterval(peak);
        const roundedMax = interval !== undefined ? Math.ceil(peak / interval) * interval : undefined;
        return {
            type: 'value',
            min: 0,
            ...(descriptor?.position ? { position: descriptor.position } : index === 1 ? { position: 'right' } : {}),
            ...(descriptor?.name ? { name: descriptor.name, nameLocation: 'middle', nameGap: showLabels ? AXIS_NAME_GAP : 16, nameTextStyle: { fontSize: 12.8 } } : {}),
            ...(descriptor?.max !== undefined ? { max: descriptor.max } : {}),
            ...(interval !== undefined ? { interval, max: roundedMax } : {}),
            axisLabel: { show: showLabels, fontSize: 11,
                formatter: descriptor?.format ? yAxisFormatter(descriptor.format) : axisNumberLabel },
            axisLine: { show: false },
            axisTick: { show: false },
            ...(index === 1 ? { splitLine: { show: false } } : {}),
        };
    });
};

const buildThresholdSeries = (thresholds: EChartThreshold[]) => ({
    type: 'line',
    data: [],
    silent: true,
    animation: false,
    tooltip: { show: false },
    markLine: {
        silent: true,
        symbol: 'none',
        animation: false,
        emphasis: { disabled: true },
        data: thresholds.map(threshold => ({
            yAxis: threshold.value,
            lineStyle: { color: threshold.color, width: 1, type: 'dashed' },
            label: {
                show: true,
                position: 'insideEndTop',
                formatter: threshold.label,
                color: threshold.color,
                fontSize: 11,
                fontWeight: 'bold',
            },
        })),
    },
});

export const buildTimeSeriesOption = (args: TimeSeriesOptionArgs): EChartsCoreOption => {
    const { min, max, stepMs } = getAxisExtent(args.timeRangeContext);
    const timezone = args.timeRangeContext.timeRange?.timezone ?? resolveUserTimezone();
    const labelFormat = axisLabelFormat((max ?? 0) - (min ?? 0));
    const plotLines = args.plotLines ?? [];
    const showLegend = !!args.legend;
    const seriesOrder = args.series.map(set => set.name);
    const hiddenFromTooltip = new Set(
        args.series.filter(set => set.chartType === 'band')
            .flatMap(set => [set.name, `${BAND_BASE_PREFIX}${set.name}`]));

    const built = buildSeries(args.series, stepMs);
    const dataSeries = roundExposedBarSegments(args.reverseStacks === false ? built : reverseStacks(built));
    const extent = args.xExtent && args.xExtent !== 'data' ? args.xExtent : { min, max };
    const axisTicks = args.xExtent === 'data'
        ? []
        : buildAxisTicks(extent.min, extent.max, stepMs, timezone);
    const series = [
        ...dataSeries,
        ...(plotLines.length > 0 ? [buildPlotLineSeries(plotLines)] : []),
        ...(args.thresholds && args.thresholds.length > 0 ? [buildThresholdSeries(args.thresholds)] : []),
    ];

    return {
        animation: false,
        grid: {
            left: hasNamedAxis(args.yAxis, 'left') ? AXIS_NAME_RESERVE : 2,
            right: hasNamedAxis(args.yAxis, 'right') ? AXIS_NAME_RESERVE : 12,
            top: 10,
            bottom: showLegend ? LEGEND_GRID_RESERVE : 0,
            containLabel: true,
        },
        xAxis: {
            type: 'time',
            containShape: false,
            ...(args.xExtent === 'data' ? {} : args.xExtent ? args.xExtent : { min, max }),
            splitNumber: AXIS_SPLIT_NUMBER,
            ...(axisTicks.length > 1 ? { axisTick: { customValues: axisTicks } } : {}),
            splitLine: { show: false },
            axisLabel: {
                fontSize: 11,
                margin: 9,
                hideOverlap: true,
                ...(axisTicks.length > 1 ? { customValues: axisTicks } : {}),
                formatter: (value: number) => formatAxisLabel(value, labelFormat, timezone),
            },
        },
        yAxis: buildYAxis(args.yAxis, args.series),
        tooltip: {
            ...TOOLTIP_BASE,
            trigger: 'axis',
            extraCssText: 'box-shadow:none; white-space:nowrap;',
            position: positionTooltip,
            formatter: args.tooltipFormatter
                ? (params: unknown) => args.tooltipFormatter!(params as never)
                : (params: unknown) => {
                    const list = Array.isArray(params) ? params : [params];
                    const visible = list.filter((param: TooltipParamLike) => !hiddenFromTooltip.has(param.seriesName ?? ''));
                    return formatTimeSeriesTooltip(visible, args.tooltipType, seriesOrder);
                },
            axisPointer: {
                type: 'line',
                lineStyle: { width: 15, color: 'rgba(125, 160, 200, 0.15)', type: 'solid' },
            },
        },
        legend: {
            show: showLegend,
            type: 'scroll',
            orient: 'horizontal',
            bottom: 0,
            left: 'center',
            height: LEGEND_MAX_HEIGHT,
            itemGap: args.series.length > 15 ? 4 : 10,
            itemWidth: 14,
            itemHeight: 10,
            data: args.series.map(set => ({
                name: set.name,
                icon: set.chartType === 'line' ? 'inherit' : 'rect',
            })),
            textStyle: { fontSize: 12, fontWeight: 'bold' },
        },
        ...(args.brush === false ? {} : {
            toolbox: { show: false, feature: { brush: { type: ['lineX'] } } },
            brush: {
                xAxisIndex: 0,
                brushType: 'lineX',
                brushMode: 'single',
                transformable: false,
                throttleType: 'debounce',
                throttleDelay: 0,
                removeOnClick: true,
                brushStyle: { color: 'rgba(125, 160, 200, 0.25)', borderColor: 'rgba(125, 160, 200, 0.5)' },
            },
        }),
        series,
    };
};
